// src/components/extensions/RegexDemo.tsx
//
// An animated demonstration of the regex explainer's behaviour: a pointer comes
// to rest on a pattern, and the editor explains it token by token.
//
// This demo is not a reskin of the fold demo and does not share its mechanics.
// What it has to show is different in kind. Folding is a single state change on
// one region; explaining is a walk through six tokens, and the interesting part
// is the correspondence between the pattern and the prose. So the motion here is
// a pointer arriving, the card opening, and the highlight stepping down the
// pattern in step with the rows appearing below it — a reader should be able to
// look at the glowing token and the highlighted row and see they are the same
// thing.
//
// The pattern is defined once, as a list of segments, and both the code and the
// card's own copy of it are rendered from that list. Building the string by hand
// as well would be the kind of duplication that drifts: change one character in
// the code sample and the breakdown would quietly start explaining a pattern
// that is no longer the one on screen.
//
// Motion notes: the walk plays once and then stops, leaving every token
// explained. It does not loop, because a highlight that keeps jumping is the most
// distracting thing a demo like this could do, and about/motion.ts rules out idle
// motion for the same reason. Under reduced motion nothing plays: the hint and
// the pattern are shown, and selecting the pattern reveals the whole breakdown
// at once.
import { useEffect, useId, useRef, useState } from 'react';
import { AnimatePresence, useInView, useReducedMotion } from 'framer-motion';
import { VscRegex, VscQuestion } from 'react-icons/vsc';

import { DURATION, EASE_OUT, motionSet } from '../about/motion';
import {
  CodeArea,
  CodeLine,
  CodeText,
  EditorFrame,
  FileKind,
  GutterSpacer,
  LineNumber,
  TabBar,
} from './editor.styles';
import {
  Breakdown,
  BreakdownRow,
  Delimiter,
  Explain,
  HoverCard,
  HoverHeader,
  HoverHint,
  HoverPattern,
  PatternButton,
  Pointer,
  Token,
  TokenChip,
  TokenKind,
} from './RegexDemo.styles';

/** What a segment is, which drives the small category label on its row. */
type SegmentKind = 'delimiter' | 'anchor' | 'class' | 'quantifier' | 'flag';

interface Segment {
  /** Exactly as it appears in the code. */
  text: string;
  kind: SegmentKind;
  /** Absent on the delimiters, which are not explained. */
  explain?: string;
}

/**
 * The sample pattern, split.
 *
 * A slug rule, because it is the pattern people actually reach for, and because
 * its six tokens are each worth a sentence without any of them being obscure.
 * The `i` flag is doing real work here, which matters: the extension explains
 * flags too, and a flag that silently changes nothing would not show that off.
 */
const SEGMENTS: readonly Segment[] = [
  { text: '/', kind: 'delimiter' },
  {
    text: '^',
    kind: 'anchor',
    explain: 'Anchors the match to the very start of the string.',
  },
  {
    text: '[a-z]',
    kind: 'class',
    explain: 'The first character has to be a letter.',
  },
  {
    text: '[a-z0-9_]',
    kind: 'class',
    explain: 'Every character after it is a letter, a digit or an underscore.',
  },
  {
    text: '*',
    kind: 'quantifier',
    explain: 'That class may repeat any number of times, including not at all.',
  },
  {
    text: '$',
    kind: 'anchor',
    explain: 'Anchors the match to the end, so nothing may follow it.',
  },
  { text: '/', kind: 'delimiter' },
  {
    text: 'i',
    kind: 'flag',
    explain: 'Case-insensitive: a capital A in the pattern also matches a lowercase a.',
  },
];

/** The segments that carry an explanation, in order. */
const EXPLAINED = SEGMENTS.filter((segment) => segment.explain);

/**
 * The pattern as one string, derived rather than written. This is what keeps the
 * code sample and the card's copy from being able to disagree.
 */
const PATTERN = SEGMENTS.map((segment) => segment.text).join('');

const SAMPLE = {
  fileName: 'slug.ts',
  /** The line the pattern lives on, before the pattern itself. */
  prefix: 'export const SLUG = ',
  suffix: ';',
  /** A second line, so the sample reads as a file rather than a snippet. */
  contextLine: 'export const isSlug = (value: string) => SLUG.test(value);',
} as const;

/** How long before the pointer arrives and the card opens, in ms. */
const OPEN_DELAY = 500;

/** How long each token is held before the walk moves on, in ms. */
const TOKEN_STEP = 420;

/**
 * The whole walk as one number, which keeps the highlight, the revealed rows and
 * the card's visibility derived from a single source rather than from three
 * states that have to be kept in agreement.
 *
 *   -1          closed
 *   0 .. n-1    the walk, standing on the token at that index
 *   n           done: everything explained, nothing highlighted
 */
type Step = number;

const CLOSED: Step = -1;
const DONE: Step = EXPLAINED.length;

const RegexDemo = () => {
  const reduceMotion = useReducedMotion() ?? false;
  const anim = motionSet(reduceMotion);

  const frameRef = useRef<HTMLDivElement>(null);
  const cardId = `regex-demo-card-${useId()}`;

  const [step, setStep] = useState<Step>(CLOSED);
  /**
   * Bumped to start a walk. A counter rather than a boolean so the demo can be
   * replayed — the second visit to this card should be able to run again, and a
   * boolean would have nothing to re-trigger on.
   */
  const [plays, setPlays] = useState(0);

  const inView = useInView(frameRef, { once: true, amount: 0.5 });

  // Autoplay on first arrival only; the counter is not bumped again by this
  // effect, so it cannot fight with a manual replay.
  const autoplayed = useRef(false);
  useEffect(() => {
    if (!inView || autoplayed.current) return;
    autoplayed.current = true;
    setPlays((count) => count + 1);
  }, [inView]);

  useEffect(() => {
    if (plays === 0) return undefined;

    if (reduceMotion) {
      // No walk, no pointer, no stagger. The whole breakdown is simply there.
      setStep(DONE);
      return undefined;
    }

    const timers: number[] = [
      window.setTimeout(() => setStep(0), OPEN_DELAY),
    ];

    EXPLAINED.forEach((_, index) => {
      if (index === 0) return;
      timers.push(
        window.setTimeout(
          () => setStep(index),
          OPEN_DELAY + index * TOKEN_STEP,
        ),
      );
    });

    // Park on DONE rather than looping: the last token gets the same time as the
    // rest, and then the highlight lifts and the explanation is left standing.
    timers.push(
      window.setTimeout(() => setStep(DONE), OPEN_DELAY + DONE * TOKEN_STEP),
    );

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [plays, reduceMotion]);

  const isOpen = step !== CLOSED;
  const activeIndex = step >= 0 && step < DONE ? step : -1;
  /** How many rows the walk has reached, which is what makes the list build up
   *  rather than appearing all at once and then being walked through. */
  const revealed = step < 0 ? 0 : Math.min(step + 1, DONE);

  /**
   * Open by walking, close if already open. Replaying on reopen rather than
   * jumping straight to the full breakdown is the point: the correspondence
   * between token and row is the thing worth watching, and it only exists during
   * the walk.
   */
  const toggle = () => {
    if (isOpen) {
      setStep(CLOSED);
      return;
    }
    setPlays((count) => count + 1);
  };

  return (
    <EditorFrame ref={frameRef}>
      <TabBar>
        <VscRegex aria-hidden="true" />
        <span>
          {SAMPLE.fileName.split('.', 1)[0]}.<FileKind>{SAMPLE.fileName.split('.').slice(1).join('.')}</FileKind>
        </span>
      </TabBar>

      <CodeArea>
        <CodeLine>
          <LineNumber>1</LineNumber>
          <GutterSpacer aria-hidden="true" />
          <CodeText>
            {SAMPLE.prefix}
            <PatternButton
              type="button"
              onClick={toggle}
              aria-expanded={isOpen}
              aria-controls={cardId}
              aria-label={`Explain the pattern ${PATTERN}`}
            >
              <AnimatePresence>
                {isOpen && (
                  <Pointer
                    key="pointer"
                    initial={{ opacity: 0, x: -10, y: -8 }}
                    animate={{ opacity: 1, x: 0, y: 0 }}
                    transition={{ duration: DURATION.micro, ease: EASE_OUT }}
                  />
                )}
              </AnimatePresence>

              {SEGMENTS.map((segment, index) => {
                if (segment.kind === 'delimiter') {
                  return (
                    <Delimiter key={`${segment.text}-${index}`}>{segment.text}</Delimiter>
                  );
                }

                // Index within EXPLAINED, not within SEGMENTS, so the highlight
                // lines up with the row it is meant to be pointing at.
                const explainedIndex = EXPLAINED.indexOf(segment);

                return (
                  <Token
                    key={`${segment.text}-${index}`}
                    $active={activeIndex === explainedIndex}
                  >
                    {segment.text}
                  </Token>
                );
              })}
            </PatternButton>
            {SAMPLE.suffix}
          </CodeText>
        </CodeLine>

        <CodeLine>
          <LineNumber>2</LineNumber>
          <GutterSpacer aria-hidden="true" />
          <CodeText>{SAMPLE.contextLine}</CodeText>
        </CodeLine>

        <AnimatePresence mode="wait" initial={false}>
          {isOpen ? (
            <HoverCard
              key="card"
              id={cardId}
              initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
              transition={{ duration: anim.micro, ease: EASE_OUT }}
            >
              <HoverHeader>
                <VscRegex aria-hidden="true" />
                Explanation
              </HoverHeader>

              <HoverPattern>{PATTERN}</HoverPattern>

              <Breakdown aria-label={`Breakdown of ${PATTERN}`}>
                {/* Only the rows the walk has reached are mounted, so the
                    breakdown is built as the highlight steps down the pattern.
                    AnimatePresence emits no element of its own, so the rows stay
                    direct children of the list. */}
                <AnimatePresence>
                  {EXPLAINED.slice(0, revealed).map((segment, index) => (
                    <BreakdownRow
                      key={`${segment.text}-${index}`}
                      $active={activeIndex === index}
                      initial={reduceMotion ? { opacity: 0 } : { opacity: 0, x: -6 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: anim.micro,
                        ease: EASE_OUT,
                      }}
                    >
                      <TokenChip>{segment.text}</TokenChip>
                      <Explain>
                        <TokenKind>{segment.kind}</TokenKind>
                        {segment.explain}
                      </Explain>
                    </BreakdownRow>
                  ))}
                </AnimatePresence>
              </Breakdown>
            </HoverCard>
          ) : (
            <HoverHint
              key="hint"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: anim.micro, ease: EASE_OUT }}
            >
              <VscQuestion aria-hidden="true" />
              Select the pattern to see it explained
            </HoverHint>
          )}
        </AnimatePresence>
      </CodeArea>
    </EditorFrame>
  );
};

export default RegexDemo;
